// src/components/WorldCoverageMap.jsx
import React, { useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Graticule,
  Sphere,
  Marker,
} from "react-simple-maps";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/** Never highlight these (dataset names) */
const EXCLUDED_LIST = [
  "Afghanistan",
  "Libya",
  "Yemen",
  "Turkmenistan",
  "Chad",
  "Myanmar",
  "Brazil",
  "Suriname",
  "Venezuela",
];

/** Normalizer */
const norm = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Your UI label -> dataset label (for countries where the dataset uses a different official name)
 */
const UI_TO_DATASET = {
  "Côte d’Ivoire": "Ivory Coast",
  "Cabo Verde": "Cape Verde",
  Eswatini: "Swaziland",
  "Timor-Leste": "East Timor",
  "Palestine (West Bank and Gaza)": "Palestine",
  Micronesia: "Federated States of Micronesia",
  "North Macedonia": "Macedonia",
  "The Bahamas": "Bahamas",
  "Congo (DRC)": "Democratic Republic of the Congo",
  Gambia: "The Gambia",
};

/**
 * Dataset short/alt name -> canonical dataset name
 * (world-atlas / Natural Earth often abbreviates the "name" field)
 */
const DS_NAME_ALIASES = {
  // Africa & MENA
  "dem. rep. congo": "democratic republic of the congo",
  "congo (kinshasa)": "democratic republic of the congo",
  "congo, dem. rep.": "democratic republic of the congo",
  "congo (brazzaville)": "republic of the congo",
  "central african rep.": "central african republic",
  "s. sudan": "south sudan",
  "eq. guinea": "equatorial guinea",
  "united rep. tanzania": "tanzania",
  "the gambia": "the gambia",

  // Europe & Eurasia
  "bosnia and herz.": "bosnia and herzegovina",
  "north macedonia": "macedonia",

  // Asia & Pacific
  "east timor": "east timor",
  "lao pdr": "laos",
  "federated states of micronesia": "federated states of micronesia",
  "marshall is.": "marshall islands",
  "solomon is.": "solomon islands",

  // Americas
  "dominican rep.": "dominican republic",
};

/** Reverse map (dataset -> UI) so tooltips show your label */
const DATASET_TO_UI_BASE = Object.entries(UI_TO_DATASET).reduce(
  (acc, [ui, ds]) => {
    acc[norm(ds)] = ui;
    return acc;
  },
  {}
);

/**
 * Fallback markers for micro-states that are too small to render at 110m resolution.
 * lon, lat (NOT lat, lon).
 */
const FALLBACK_MARKERS = {
  Barbados: [-59.5432, 13.1939],
  // (Add more tiny islands here if you ever list them)
};

export default function WorldCoverageMap({ countriesByRegion }) {
  const [tooltip, setTooltip] = useState("");
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  // Build coverage directly from the website list
  const {
    datasetCoverageSet,
    datasetExcludedSet,
    datasetToUi,
    uiList,
    markerUiList,
  } = useMemo(() => {
    const uiNames = countriesByRegion
      ? Object.values(countriesByRegion).flat().map((c) => c.name)
      : [];

    // Translate UI -> dataset where needed
    const datasetNames = uiNames.map((ui) => UI_TO_DATASET[ui] || ui);

    const coverageSet = new Set(datasetNames.map((n) => norm(n)));
    const excludedSet = new Set(EXCLUDED_LIST.map((n) => norm(n)));

    // dataset -> UI for tooltips
    const d2u = { ...DATASET_TO_UI_BASE };
    uiNames.forEach((ui) => {
      const ds = UI_TO_DATASET[ui] || ui;
      d2u[norm(ds)] = ui;
    });

    // Which UI countries should get fallback markers?
    const markers = uiNames.filter((ui) => FALLBACK_MARKERS[ui]);

    return {
      datasetCoverageSet: coverageSet,
      datasetExcludedSet: excludedSet,
      datasetToUi: d2u,
      uiList: uiNames,
      markerUiList: markers,
    };
  }, [countriesByRegion]);

  /** Canonicalize a dataset-provided name (apply DS aliases) */
  const canonicalizeDatasetName = (datasetNameRaw) => {
    const n0 = norm(datasetNameRaw);
    return norm(DS_NAME_ALIASES[n0] || n0);
  };

  const isCoveredDatasetName = (datasetNameRaw) => {
    const n = canonicalizeDatasetName(datasetNameRaw);
    if (datasetExcludedSet.has(n)) return false;
    return datasetCoverageSet.has(n);
  };

  const uiDisplayName = (datasetNameRaw) => {
    const n = canonicalizeDatasetName(datasetNameRaw);
    if (datasetToUi[n]) return datasetToUi[n];
    return datasetNameRaw || "";
  };

  return (
    <div className="w-full max-w-xl mx-auto rounded-3xl bg-white shadow p-3 relative">
      <ComposableMap
        projection="geoMercator"
        width={800}
        height={420}
        style={{ width: "100%", height: "auto" }}
      >
        <ZoomableGroup center={[15, 10]} zoom={1}>
          <Sphere stroke="#cbd5e1" fill="#eef2ff" strokeWidth={0.5} />
          <Graticule stroke="#e2e8f0" strokeWidth={0.4} />
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const dsName =
                  geo.properties.name ||
                  geo.properties.NAME ||
                  geo.properties.ADMIN ||
                  geo.properties.NAME_LONG;

                const covered = isCoveredDatasetName(dsName);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={(e) => {
                      if (covered) {
                        setTooltip(uiDisplayName(dsName));
                        setCoords({ x: e.clientX, y: e.clientY });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (covered) setCoords({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => setTooltip("")}
                    style={{
                      default: {
                        fill: covered ? "#2563eb" : "#bfdbfe",
                        stroke: covered ? "#1e40af" : "#94a3b8",
                        strokeWidth: 0.5,
                        outline: "none",
                        cursor: covered ? "pointer" : "default",
                      },
                      hover: {
                        fill: covered ? "#1d4ed8" : "#93c5fd",
                        stroke: covered ? "#1e40af" : "#64748b",
                        strokeWidth: 0.6,
                        outline: "none",
                        cursor: covered ? "pointer" : "default",
                      },
                      pressed: {
                        fill: covered ? "#1d4ed8" : "#93c5fd",
                        outline: "none",
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Fallback markers for tiny countries that don’t have polygons at 110m (e.g., Barbados) */}
          {markerUiList.map((uiName) => {
            const coords = FALLBACK_MARKERS[uiName];
            if (!coords) return null;
            return (
              <Marker key={`mk-${uiName}`} coordinates={coords}>
                <circle
                  r={3.2}
                  fill="#2563eb"
                  stroke="#1e40af"
                  strokeWidth={0.8}
                  onMouseEnter={(e) => {
                    setTooltip(uiName);
                    setCoords({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) =>
                    setCoords({ x: e.clientX, y: e.clientY })
                  }
                  onMouseLeave={() => setTooltip("")}
                  style={{ cursor: "pointer" }}
                />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Caption + tiny legend */}
      <div className="flex items-center justify-between text-[11px] text-slate-500 px-2 pb-1">
        <span>Where Can Venturly Global Take You?</span>
        <span className="inline-flex items-center">
          <span
            className="inline-block w-3 h-3 rounded-sm mr-1"
            style={{ background: "#2563eb" }}
          />
          Venturly Global Consultant Coverage
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 px-2 py-1 rounded bg-black/80 text-white text-xs"
          style={{ left: coords.x + 12, top: coords.y + 12 }}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
}
